import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';
import { useForm } from 'react-hook-form';
import { CreateChancel, CustomizedSwitch, Input, NumericFormatCustom, SelectAutocompleteMultiple } from 'components';
import { fundingSourceActions, httpRequestsOnErrorsActions, httpRequestsOnSuccessActions } from 'store';
import { foundingSourceModalStyle } from './styles';
import { Colors, enumValues, FindErrorItem, FindLoad, FindSuccessItem, useModal } from 'utils';
import { credentialMatchHelperText, CredentialMatchMode } from '../constants';

const GET_PAY_CODES = 'GET_PAY_CODE_GLOBAL';
const GET_CREDENTIALS = 'GET_CREDENTIAL_GLOBAL';
export const FundingSourceModifiersAdd = ({ info, currentService }) => {
    const ACTION_TYPE = !!info ? 'EDIT_FUNDING_MODIFIER' : 'CREATE_FUNDING_MODIFIER';
    const classes = foundingSourceModalStyle();
    const { close } = useModal();
    const params = useParams();
    const dispatch = useDispatch();
    const success = FindSuccessItem(ACTION_TYPE);
    const loader = FindLoad(ACTION_TYPE);
    const backError = FindErrorItem(ACTION_TYPE);
    const credentials = useSelector((state) => state.system.credentials);
    const payCodes = useSelector((state) => state.payroll.payCodes);
    const payCodeOptions = useMemo(() => {
        const list = Array.isArray(payCodes) ? payCodes : [];
        if (!info?.payCodeTypeId || list.some((code) => code?.id === info.payCodeTypeId)) return list;
        return [...list, { id: info.payCodeTypeId, name: info?.payCodeTypeName }];
    }, [payCodes, info?.payCodeTypeId, info?.payCodeTypeName]);

    const { handleSubmit, control, setError, clearErrors, reset, watch, setValue } = useForm({
        defaultValues: {
            credentialIds: [],
            // Matches the schema default — existing charge rates keep the original "every credential" rule.
            credentialMatch: CredentialMatchMode.ALL,
            type: '',
            // needsSupervision: false,
            // actsAsSupervision: false,
        },
    });
    const credentialMatch = watch('credentialMatch');

    useEffect(() => {
        if (info) {
            const result = {
                ...info,
            };
            result.credentialIds = info?.credentials?.map((credential) => {
                return { ...credential, id: credential?._id, name: credential?.name };
            });
            result.credentialMatch = info?.credentialMatch || CredentialMatchMode.ALL;
            reset(result);
        }
    }, [info]);

    useEffect(() => {
        if (backError?.error === 'Modifier already exists') {
            setError('name', {
                message: 'Modifier with this name already exists',
            });
            dispatch(httpRequestsOnSuccessActions.removeSuccess(ACTION_TYPE));
        }
    }, [backError]);

    useEffect(() => {
        if (success) {
            close();
            dispatch(httpRequestsOnSuccessActions.removeSuccess(ACTION_TYPE));
        }
    }, [success]);

    useEffect(() => {
        return () => {
            clearErrors();
            dispatch(httpRequestsOnErrorsActions.removeError(ACTION_TYPE));
        };
    }, []);

    function handleCreate(data) {
        const modifierDate = {
            name: data.name,
            billedRate: +data.billedRate,
            allowedRate: +data.allowedRate,
            credentialIds: data?.credentialIds?.map((credential) => credential?.id || credential?._id),
            credentialMatch: data?.credentialMatch || CredentialMatchMode.ALL,
            serviceId: currentService?.id,
            modifierTag: data?.modifierTag ? data?.modifierTag : null,
            modifierTag2: data?.modifierTag2 ? data?.modifierTag2 : null,
            modifierTag3: data?.modifierTag3 ? data?.modifierTag3 : null,
            payCodeTypeId: data?.payCodeTypeId ? data?.payCodeTypeId : null,
            // needsSupervision: !!data.needsSupervision,
            // actsAsSupervision: !!data.actsAsSupervision,
        };
        data?.region ? (modifierDate.region = data?.region) : delete modifierDate.region;
        data.type ? (modifierDate.type = data.type) : delete modifierDate.type;

        if (!!info) {
            dispatch(fundingSourceActions.editFundingModifier(info?.id, modifierDate, currentService?.id));
        } else {
            dispatch(fundingSourceActions.createFundingModifier(params?.id, modifierDate));
        }
    }

    return (
        <div className={classes.addModifierWrapper}>
            <p className="modal-header-title">{!!info ? 'Edit Charge Rate' : 'Add Charge Rate'}</p>
            <form className={classes.modifierBoxStyle} onSubmit={handleSubmit(handleCreate)}>
                <Input
                    name="name"
                    label="Title*"
                    variant="outlined"
                    control={control}
                    rules={{
                        maxLength: {
                            value: 100,
                            message: 'Max length is 100 digits.',
                        },
                        required: 'Title is required. Please enter a title for the charge rate.',
                    }}
                />
                <div className='flex-align-center' style={{gap: 16}}>
                <Input
                    name="modifierTag"
                    label="Modifier 1"
                    variant="outlined"
                    maxLength={40}
                    control={control}
                    rules={{
                        maxLength: {
                            value: 40,
                            message: 'Max length is 40 digits.',
                        },
                    }}
                />
                <Input
                    name="modifierTag2"
                    label="Modifier 2"
                    variant="outlined"
                    maxLength={40}
                    control={control}
                    rules={{
                        maxLength: {
                            value: 40,
                            message: 'Max length is 40 digits.',
                        },
                    }}
                />
                <Input
                    name="modifierTag3"
                    label="Modifier 3"
                    variant="outlined"
                    maxLength={40}
                    control={control}
                    rules={{
                        maxLength: {
                            value: 40,
                            message: 'Max length is 40 digits.',
                        },
                    }}
                />
                </div>
                <NumericFormatCustom
                    name={'billedRate'}
                    label={'Billed Rate*'}
                    prefix={'$'}
                    thousandSeparator={true}
                    fixedDecimalScale={true}
                    decimalScale={4}
                    control={control}
                    rules={{
                        required: 'Billed Rate is required. Please enter the charge amount.',
                    }}
                />
                <NumericFormatCustom
                    name={'allowedRate'}
                    label={'Allowed Rate*'}
                    prefix={'$'}
                    thousandSeparator={true}
                    fixedDecimalScale={true}
                    decimalScale={4}
                    control={control}
                    rules={{
                        required: 'Allowed Rate is required. Please enter the charge amount.',
                    }}
                />
                <Input
                    name="region"
                    label="Region"
                    variant="outlined"
                    control={control}
                    maxLength={50}
                    rules={{
                        maxLength: {
                            value: 50,
                            message: 'Max length is 50 digits.',
                        },
                    }}
                />
                <SelectAutocompleteMultiple
                    loadType={GET_CREDENTIALS}
                    options={credentials}
                    multiple={true}
                    label={'Credentials*'}
                    placeholder={'Select the credential'}
                    name="credentialIds"
                    control={control}
                    renderValue={(options) => options?.name}
                    selectedType={'id'}
                    rules={{
                        required: 'Selecting credentials is required. Please choose at least one credential.',
                    }}
                />
                <div className={classes.credentialMatchRow}>
                    <div>
                        <p className={classes.credentialMatchTitle}>Require all credentials</p>
                        <p className={classes.credentialMatchHelper}>{credentialMatchHelperText[credentialMatch]}</p>
                    </div>
                    <CustomizedSwitch
                        size="large"
                        checkedBgColor={Colors.ThemeBlue}
                        checked={credentialMatch === CredentialMatchMode.ALL}
                        handleClick={() =>
                            setValue(
                                'credentialMatch',
                                credentialMatch === CredentialMatchMode.ALL ? CredentialMatchMode.ANY : CredentialMatchMode.ALL,
                                { shouldDirty: true },
                            )
                        }
                    />
                </div>
                <SelectAutocompleteMultiple
                    options={enumValues.FUNDING_MODIFIER_SERVICE_TYPES}
                    label={'Type'}
                    placeholder={'Type'}
                    name="type"
                    control={control}
                />
                <SelectAutocompleteMultiple
                    loadType={GET_PAY_CODES}
                    options={payCodeOptions}
                    label={'Default Paycode'}
                    placeholder={'Select Default Paycode'}
                    name="payCodeTypeId"
                    control={control}
                    renderValue={(options) => options?.name}
                    selectedType={'id'}
                />
                {/*<div className='payer-supervision-box'>*/}
                {/*    <p className='payer-supervision-title'>*/}
                {/*        Supervision*/}
                {/*    </p>*/}
                {/*    <CheckboxCustom*/}
                {/*        label={'Needs Supervision (e.g. RBT direct services)'}*/}
                {/*        name={`needsSupervision`}*/}
                {/*        control={control}*/}
                {/*        padding={'0 9px 0 0'}*/}
                {/*    />*/}
                {/*    <CheckboxCustom*/}
                {/*        label={'Acts as Supervision (e.g. BCBA oversight sessions)'}*/}
                {/*        name={`actsAsSupervision`}*/}
                {/*        control={control}*/}
                {/*        wrapperStyles={{marginTop: '8px'}}*/}
                {/*        padding={'0 9px 0 0'}*/}
                {/*    />*/}
                {/*</div>*/}

                <div className={classes.foundingSourceModalsBodyBlock}>
                    <CreateChancel
                        type="submit"
                        loader={!!loader.length}
                        create={!!info ? 'Save' : 'Add'}
                        chancel={'Cancel'}
                        onClose={close}
                        buttonWidth={'100%'}
                        createButnMargin={'16px'}
                    />
                </div>
            </form>
        </div>
    );
};
